import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FiSend } from "react-icons/fi";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";

/* ─── Animação de entrada ────────────────────────────────────────── */

const fadeSlideIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Layout da seção ────────────────────────────────────────────── */

const Secao = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 4px;
`;

const Cabecalho = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
`;

const Titulo = styled.h4`
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.04em;
    flex: 1;
`;

const Contador = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background-color: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    padding: 2px 8px;
`;

/* ─── Lista de observações ───────────────────────────────────────── */

const Lista = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 4px;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 4px;
    }
`;

const CardObs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-left: 3px solid #263b4b;
    border-radius: 0 8px 8px 0;
    animation: ${fadeSlideIn} 0.2s ease both;
`;

const ObsMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ObsAutor = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
`;

const ObsData = styled.span`
    font-size: 11px;
    color: #94a3b8;
`;

const ObsTexto = styled.p`
    margin: 0;
    font-size: 13px;
    color: #1e293b;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
`;

/* ─── Vazio / loading ────────────────────────────────────────────── */

const MsgVazia = styled.p`
    margin: 0;
    font-size: 13px;
    color: #94a3b8;
    text-align: center;
    padding: 14px 0;
    background-color: #f8fafc;
    border: 1px dashed #e2e8f0;
    border-radius: 8px;
`;

/* ─── Input de nova observação ───────────────────────────────────── */

const NovaObsWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const NovaObsLabel = styled.label`
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    padding-left: 2px;
`;

const NovaObsRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-end;
`;

const TextareaObs = styled.textarea`
    flex: 1;
    min-height: 72px;
    max-height: 140px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 13px;
    font-family: inherit;
    color: #1e293b;
    background-color: #f8fafc;
    resize: vertical;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder { color: #94a3b8; }

    &:hover { border-color: #cbd5e1; background-color: #fff; }

    &:focus {
        outline: none;
        border-color: #263b4b;
        background-color: #fff;
        box-shadow: 0 0 0 3px rgba(38,59,75,0.10);
    }

    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const BotaoEnviar = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 8px;
    cursor: pointer;
    flex-shrink: 0;
    background-color: #263b4b;
    color: #fff;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) { background-color: #1e2f3c; }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid #263b4b;
        outline-offset: 2px;
    }
`;

/* ─── Helpers ────────────────────────────────────────────────────── */

function formatarDataHora(value) {
    if (!value) return "";
    const d = new Date(String(value).replace(" ", "T"));
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

/* ─── Componente ─────────────────────────────────────────────────── */

/**
 * ObservacoesSection
 *
 * Props:
 *   atendimentoId  number   — ID do atendimento
 *   canAdd         boolean  — exibe o input de nova observação (default false)
 */
const ObservacoesSection = ({ atendimentoId, canAdd = false }) => {
    const api = useApi();
    const { notifyError, notifySuccess } = useToast();

    const [observacoes, setObservacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [novaObs, setNovaObs] = useState("");
    const [salvando, setSalvando] = useState(false);

    const listaRef = useRef(null);

    const scrollParaFim = () => {
        if (listaRef.current) {
            listaRef.current.scrollTop = listaRef.current.scrollHeight;
        }
    };

    const carregarObservacoes = async () => {
        if (!atendimentoId) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                "/Painel/API/AtendimentoAmb/Observacoes/List/index.php",
                { params: { atendimento_id: atendimentoId } }
            );
            const lista = Array.isArray(data?.data) ? data.data : [];
            setObservacoes(lista);
        } catch (err) {
            console.error("Erro ao carregar observações:", err);
            setObservacoes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarObservacoes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atendimentoId]);

    // Rola para o fim toda vez que a lista atualiza
    useEffect(() => {
        if (!loading) scrollParaFim();
    }, [observacoes, loading]);

    const handleEnviar = async () => {
        const texto = novaObs.trim();
        if (!texto || salvando) return;

        setSalvando(true);
        try {
            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/Observacoes/Save/index.php",
                { atendimento_id: atendimentoId, observacao: texto }
            );

            if (data?.success) {
                setObservacoes((prev) => [...prev, data.data]);
                setNovaObs("");
                notifySuccess(data?.message || "Observação salva.");
            } else {
                notifyError(data?.message || "Erro ao salvar observação.");
            }
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao salvar observação.");
        } finally {
            setSalvando(false);
        }
    };

    const handleKeyDown = (e) => {
        // Ctrl+Enter ou Cmd+Enter envia
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault();
            handleEnviar();
        }
    };

    return (
        <Secao>
            <Cabecalho>
                <Titulo>OBSERVAÇÕES</Titulo>
                {!loading && (
                    <Contador>
                        {observacoes.length === 0
                            ? "nenhuma"
                            : `${observacoes.length}`}
                    </Contador>
                )}
            </Cabecalho>

            {loading ? (
                <MsgVazia>Carregando observações…</MsgVazia>
            ) : observacoes.length === 0 ? (
                <MsgVazia>Nenhuma observação registrada.</MsgVazia>
            ) : (
                <Lista ref={listaRef}>
                    {observacoes.map((obs) => (
                        <CardObs key={obs.id}>
                            <ObsMeta>
                                <ObsAutor>{obs.nome_usuario ?? obs.created_by}</ObsAutor>
                                <ObsData>{formatarDataHora(obs.created_at)}</ObsData>
                            </ObsMeta>
                            <ObsTexto>{obs.observacao}</ObsTexto>
                        </CardObs>
                    ))}
                </Lista>
            )}

            {canAdd && (
                <NovaObsWrap>
                    <NovaObsLabel>NOVA OBSERVAÇÃO</NovaObsLabel>
                    <NovaObsRow>
                        <TextareaObs
                            placeholder="Digite uma observação… (Ctrl+Enter para enviar)"
                            value={novaObs}
                            onChange={(e) => setNovaObs(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={salvando}
                        />
                        <BotaoEnviar
                            type="button"
                            title="Enviar observação"
                            disabled={!novaObs.trim() || salvando}
                            onClick={handleEnviar}
                        >
                            <FiSend size={16} />
                        </BotaoEnviar>
                    </NovaObsRow>
                </NovaObsWrap>
            )}
        </Secao>
    );
};

export default ObservacoesSection;
